/**
 * workflows-store — interface com /api/v1/workflows (R4.5 multi-workflow).
 *
 * React Query hooks:
 * - useWorkflows({ dept?, ativo? }) — lista
 * - useWorkflow(id) — single
 * - useDefaultWorkflowOfDept(deptSlug) — default ativo do dept (deriva)
 * - useWorkflowActions() — create/update/delete
 *
 * Cache: 5 min staleTime (workflows mudam raramente). Invalidação após
 * mutations.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export interface WorkflowResponsavel {
  id: string;
  nome: string;
  fotoUrl: string | null;
}

export interface Workflow {
  id: string;
  deptSlug: string;
  slug: string;
  nome: string;
  descricao: string | null;
  cor: string | null;
  icone: string | null;
  ordem: number;
  defaultFlag: boolean;
  ativo: boolean;
  criadoEmIso: string;
  atualizadoEmIso: string;
  /** Bloco E (FEAT-V2-011): id do user responsável (NULL se não setado). */
  responsavelId: string | null;
  /** Shape do user (LEFT JOIN no backend). NULL se não setado ou orphan. */
  responsavel: WorkflowResponsavel | null;
}

export interface CreateWorkflowInput {
  deptSlug: string;
  slug: string;
  nome: string;
  descricao?: string | null;
  cor?: string | null;
  icone?: string | null;
  ordem?: number;
  defaultFlag?: boolean;
  ativo?: boolean;
  responsavelId?: string | null;
}

export type UpdateWorkflowInput = Partial<Omit<CreateWorkflowInput, 'deptSlug'>>;

export const WORKFLOWS_KEYS = {
  all: ['v1', 'workflows'] as const,
  list: (dept: string | null, ativo: boolean | null) =>
    ['v1', 'workflows', { dept, ativo }] as const,
  byId: (id: string) => ['v1', 'workflows', id] as const,
};

interface UseListOptions {
  dept?: string | null;
  ativo?: boolean | null;
}

export function useWorkflows(options: UseListOptions = {}) {
  const { dept = null, ativo = true } = options;
  const params = new URLSearchParams();
  if (dept) params.set('dept', dept);
  if (ativo === false) params.set('ativo', 'false');
  const qs = params.toString();
  const url = qs ? `/api/v1/workflows?${qs}` : '/api/v1/workflows';

  return useQuery({
    queryKey: WORKFLOWS_KEYS.list(dept, ativo),
    queryFn: () => api.get<Workflow[]>(url),
    enabled: hasToken(),
    staleTime: 5 * 60_000,
  });
}

export function useWorkflow(id: string | null) {
  return useQuery({
    queryKey: WORKFLOWS_KEYS.byId(id ?? ''),
    queryFn: () => api.get<Workflow>(`/api/v1/workflows/${encodeURIComponent(id!)}`),
    enabled: !!id && hasToken(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Workflow default ativo do dept (deriva de useWorkflows).
 * Útil pra redirect inicial `/menu/:dept` → `/menu/:dept/:default-workflow-slug`.
 */
export function useDefaultWorkflowOfDept(deptSlug: string | null): Workflow | null {
  const { data: list = [] } = useWorkflows({ dept: deptSlug, ativo: true });
  return useMemo(() => list.find((w) => w.defaultFlag) ?? list[0] ?? null, [list]);
}

/**
 * Resolve workflow por (dept_slug, workflow_slug) — usado pelo roteamento
 * `/menu/:dept/:workflow`. Retorna null se não existe.
 */
export function useWorkflowByDeptAndSlug(
  deptSlug: string | null,
  workflowSlug: string | null
): Workflow | null {
  const { data: list = [] } = useWorkflows({ dept: deptSlug, ativo: true });
  return useMemo(
    () => list.find((w) => w.slug === workflowSlug) ?? null,
    [list, workflowSlug]
  );
}

export function useWorkflowActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: WORKFLOWS_KEYS.all });

  const create = useCallback(
    async (input: CreateWorkflowInput): Promise<Workflow> => {
      setPending('create');
      try {
        const w = await api.post<Workflow>('/api/v1/workflows', input);
        invalidate();
        return w;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const update = useCallback(
    async (id: string, input: UpdateWorkflowInput): Promise<Workflow> => {
      setPending(id);
      try {
        const w = await api.put<Workflow>(`/api/v1/workflows/${encodeURIComponent(id)}`, input);
        invalidate();
        return w;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      setPending(id);
      try {
        await api.delete(`/api/v1/workflows/${encodeURIComponent(id)}`);
        invalidate();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  return { create, update, remove, pending };
}
